import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { TenantUserViewModel } from '../api';

/** Confirms `DELETE /tenants/{uuid}/users/{userGuid}` (deactivate, not delete — PTV2-1426). */
export function DeactivateTenantUserConfirmDialog({
  user,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly user: TenantUserViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (user: TenantUserViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = user !== null;
  const name =
    user !== null
      ? user.fullName.length > 0
        ? user.fullName
        : user.name.length > 0
          ? user.name
          : user.email
      : '';
  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.detail.users.deactivate.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.platform.detail.users.deactivate.message', { name })}
          </DialogDescription>
        </DialogHeader>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.platform.detail.users.deactivate.errors.submit', { code: submitErrorCode })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.platform.detail.users.deactivate.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (user !== null) onConfirm(user);
            }}
          >
            {t('routes.platform.detail.users.deactivate.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
